import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { BillingFeature } from 'webapp/enums/enums';

import AvatarOrInitialsComponent from 'webapp/components/avatar-or-initials';
import FormSelectWithActionComponent from 'webapp/components/form/select-with-action';

import type Goal from 'webapp/models-custom/goal';

interface GoalInlineOwnerComponentArgs {
	model: Goal;
	canEdit?: boolean;
	saveOnChange?: boolean;
	isDisabled?: boolean;
}

export default class GoalInlineOwnerComponent extends Component<GoalInlineOwnerComponentArgs> {
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('account') declare accountService: Service.Account;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('billing') declare billingService: Service.Billing;

	get model() {
		return this.args.model;
	}

	get usersList() {
		return this.mmUsersService.activatedUsersList;
	}

	get ownerUserRecord() {
		return this.model.ownerUser || null;
	}

	get ownerIdString() {
		return this.model.ownerId?.toString() ?? '';
	}

	get ownerActions() {
		if (this.currentUserService.isAdmin) {
			return [
				{
					id: '',
					text: 'Invite new user',
					isSpecial: true,
					position: 'bottom',
					onClick: () => {
						if (this.currentUserService.isVerified) {
							if (this.billingService.checkIfCan(BillingFeature.USERS, true)) {
								this.mmUsersService.openUserInvitePopup({
									maximumNumberOfNewUsers: 1,
									onUserInvited: (userRecords) => {
										const invitedUser = userRecords[0];
										if (invitedUser) {
											this.changeOwner(String(invitedUser.id));
										}
									},
								});
							}
						} else {
							this.currentUserService.openEmailVerificationInfoPopup(
								'invite new users',
							);
						}
					},
					html: '<a role="button" class="invite"><ui-icon name="Plus" size="sm"></ui-icon> Invite new user</a>',
				},
			];
		}

		return [];
	}

	@action
	changeOwner(value: string | null): void {
		this.model.ownerId = value ? +value : null;

		if (this.args.saveOnChange) {
			void this.model.save();
		}
	}
	<template>
		{{#if @canEdit}}
			<FormSelectWithActionComponent
				name="ownerId"
				@selected={{this.ownerIdString}}
				@options={{this.usersList}}
				@actionOptions={{this.ownerActions}}
				@onChange={{this.changeOwner}}
				@placeholder="Please select"
				@showFloatlabel={{false}}
				@showLabel2={{false}}
				@isNumeric={{false}}
				@isRequired={{true}}
				@isDisabled={{@isDisabled}}
			/>
		{{else}}
			<AvatarOrInitialsComponent
				@name={{this.ownerUserRecord.name}}
				@avatar={{this.ownerUserRecord.avatar}}
			/>

			<span>{{this.ownerUserRecord.name}}</span>
		{{/if}}
	</template>
}
